import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Skeleton,
} from "@/components/ui";
import { useParams } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useRxEligibility } from "../Eligibility/network/useRxEligibility";
import { CopayTable } from "./CopayTable";
import {
  PayerAlternatives,
  payerAlternativePreferenceLevel,
} from "./PayerAlternatives";
import { TherapeuticAlternatives } from "./TherapeuticAlternatives";
import type { FormularyLookupResponse } from "./network/useFormularyLookup";
import { useFormularyLookup } from "./network/useFormularyLookup";
import type { AgeLimitRestriction, QuantityLimitRestriction } from "./types";

const formatAgeRange = (al: AgeLimitRestriction): string => {
  if (al.minimumAge != null && al.maximumAge != null) {
    return `${al.minimumAge} ${al.minimumAgeUnit ?? ""} – ${al.maximumAge} ${al.maximumAgeUnit ?? ""}`.trim();
  }
  if (al.minimumAge != null) {
    return `≥ ${al.minimumAge} ${al.minimumAgeUnit ?? ""}`.trim();
  }
  if (al.maximumAge != null) {
    return `≤ ${al.maximumAge} ${al.maximumAgeUnit ?? ""}`.trim();
  }
  return "";
};

const formatQuantityLimit = (ql: QuantityLimitRestriction): string =>
  `• Max ${ql.amountUnit ?? ""}: ${ql.maximumAmount ?? "—"} per ${ql.maximumTimePeriodUnits ?? ""} ${ql.timePeriodUnit ?? ""}`.trim();

interface FormularyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ndc: string;
  medicationName?: string;
}

const FormularyData = ({ data }: { data: FormularyLookupResponse }) => {
  const copays = data.copays ?? [];
  // Alternatives are not permitted when the drug's formulary status is unknown.
  const isUnknown =
    data.formularyStatus == null ||
    data.formularyStatus?.toLowerCase() === "unknown";

  // Payer-defined alternatives sorted by stated preference level (ascending = most preferred first).
  // Shown for all non-unknown drugs, including on-formulary/preferred.
  const alternatives = isUnknown
    ? []
    : (data.alternatives ?? [])
        .slice()
        .sort(
          (a, b) =>
            payerAlternativePreferenceLevel(a) -
            payerAlternativePreferenceLevel(b),
        );

  // Therapeutic alternatives are not shown if the plan has a payer alternative file with a match.
  const therapeuticAlts =
    isUnknown || alternatives.length > 0
      ? []
      : (data.therapeuticAlternatives ?? []);

  const restrictions = data.restrictions;
  const showQuantityLimit =
    restrictions?.hasQuantityLimit === true ||
    (restrictions?.quantityLimitRestrictions ?? []).length > 0;
  const showAgeLimit =
    restrictions?.hasAgeLimit === true ||
    (restrictions?.ageLimitRestrictions ?? []).length > 0;

  const hasRestrictions =
    restrictions?.hasPriorAuthorization != null ||
    (restrictions?.messages ?? []).length > 0 ||
    showQuantityLimit ||
    showAgeLimit ||
    restrictions?.genderRestriction != null ||
    restrictions?.hasStepTherapy === true ||
    (restrictions?.drugResources ?? []).length > 0;
  const hasNoData =
    copays.length === 0 &&
    alternatives.length === 0 &&
    therapeuticAlts.length === 0 &&
    !hasRestrictions;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        {data.formularyStatus && (
          <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">
            {data.formularyStatus}
          </span>
        )}
        {data.brand !== null && (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
            {data.brand ? "Brand" : "Generic"}
          </span>
        )}
        {data.otc != null && (
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
            {data.otc ? "OTC" : "Rx only"}
          </span>
        )}
        {restrictions?.hasPriorAuthorization != null && (
          <span
            className={`rounded px-1.5 py-0.5 text-xs ${
              restrictions.hasPriorAuthorization
                ? "bg-red-100 text-red-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {restrictions.hasPriorAuthorization
              ? "Prior Auth Required"
              : "No Prior Auth"}
          </span>
        )}
        {restrictions?.hasStepTherapy === true && (
          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">
            Step Therapy Required
          </span>
        )}
      </div>

      {(restrictions?.messages ?? []).length > 0 && (
        <Callout title="Messages" variant="warning">
          <BodyText size="sm" className="whitespace-pre-line">
            {(restrictions?.messages ?? [])
              .filter((msg) => msg.message != null)
              .map((msg) => `• ${msg.message}`)
              .join("\n")}
          </BodyText>
        </Callout>
      )}

      {showQuantityLimit && (
        <Callout title="Quantity Limits" variant="info">
          <BodyText size="sm" className="whitespace-pre-line">
            {(restrictions?.quantityLimitRestrictions ?? [])
              .map(formatQuantityLimit)
              .join("\n")}
          </BodyText>
        </Callout>
      )}

      {showAgeLimit && (
        <Callout title="Age Limits" variant="info">
          <BodyText size="sm" className="whitespace-pre-line">
            {(restrictions?.ageLimitRestrictions ?? [])
              .map((al) => formatAgeRange(al))
              .filter(Boolean)
              .map((range) => `• ${range}`)
              .join("\n")}
          </BodyText>
        </Callout>
      )}

      {restrictions?.genderRestriction != null && (
        <Callout title="Gender Restriction" variant="info">
          <BodyText size="sm">{restrictions.genderRestriction}</BodyText>
        </Callout>
      )}

      {(restrictions?.stepMedicationRestrictions ?? []).length > 0 && (
        <div className="flex flex-col gap-2">
          <BodyText size="sm" className="font-medium">
            Step Medications (in order)
          </BodyText>
          <div className="rounded-md border overflow-hidden">
            {(restrictions?.stepMedicationRestrictions ?? [])
              .slice()
              .sort(
                (a, b) =>
                  (a.stepOrder ?? Number.POSITIVE_INFINITY) -
                  (b.stepOrder ?? Number.POSITIVE_INFINITY),
              )
              .map((sm, i) => (
                <div
                  key={sm.stepOrder ?? i}
                  className="text-sm py-1.5 px-3 border-b last:border-0"
                >
                  {i + 1}. {sm.displayName ?? sm.ndc ?? "—"}
                </div>
              ))}
          </div>
        </div>
      )}

      {(restrictions?.drugResources ?? []).length > 0 && (
        <Callout title="Drug Resources" variant="info">
          <BodyText size="sm" className="flex flex-col gap-1">
            {(restrictions?.drugResources ?? []).map((r, i) => (
              <span key={r.url ?? r.resourceType ?? i}>
                {r.resourceType}
                {r.url && (
                  <>
                    {": "}
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {r.url}
                    </a>
                  </>
                )}
              </span>
            ))}
          </BodyText>
        </Callout>
      )}

      <CopayTable copays={copays} />
      <PayerAlternatives alternatives={alternatives} />
      <TherapeuticAlternatives
        alternatives={therapeuticAlts}
        selectedFormularyStatus={data.formularyStatus}
      />

      {hasNoData && (
        <BodyText size="sm" textColor="tertiary">
          No formulary data available.
        </BodyText>
      )}
    </div>
  );
};

interface FormularyResultsProps {
  patientId: string;
  patientEligibilityId: number;
  ndc: string;
  payerName: string;
  onBack: () => void;
}

const FormularyResults = ({
  patientId,
  patientEligibilityId,
  ndc,
  payerName,
  onBack,
}: FormularyResultsProps) => {
  const { data, isPending, isError, refetch } = useFormularyLookup({
    patientId,
    patientEligibilityId,
    ndc,
    enabled: true,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="icon"
          className="m-0 p-0"
          onClick={onBack}
          aria-label="Back to payer selection"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <BodyText size="sm" textColor="tertiary">
          {payerName}
        </BodyText>
      </div>

      {isPending && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col gap-3">
          <Callout
            variant="danger"
            title="Error loading formulary"
            description="Unable to retrieve formulary information."
          />
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {data && <FormularyData data={data} />}
    </div>
  );
};

interface SelectedPayer {
  patientEligibilityId: number;
  payerName: string;
}

export const FormularyModal = ({
  open,
  onOpenChange,
  ndc,
  medicationName,
}: FormularyModalProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [selectedPayer, setSelectedPayer] = useState<SelectedPayer | null>(
    null,
  );

  const { data, isLoading, isError, refetch } = useRxEligibility({
    patientId,
    enabled: open,
  });

  const handleClose = (value: boolean) => {
    onOpenChange(value);
    if (!value) setSelectedPayer(null);
  };

  const renderContent = () => {
    if (selectedPayer) {
      return (
        <FormularyResults
          key={selectedPayer.patientEligibilityId}
          patientId={patientId}
          patientEligibilityId={selectedPayer.patientEligibilityId}
          ndc={ndc}
          payerName={selectedPayer.payerName}
          onBack={() => setSelectedPayer(null)}
        />
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-col gap-3">
          <Callout
            variant="danger"
            title="Error loading payers"
            description="Unable to retrieve eligibility information."
          />
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      );
    }

    if (data && data.coverages.length === 0) {
      return (
        <BodyText size="sm" textColor="tertiary">
          No payers found. Eligibility information is required to look up the
          formulary.
        </BodyText>
      );
    }

    if (data) {
      return (
        <div className="flex flex-col gap-2">
          <BodyText size="sm" textColor="tertiary">
            Select a payer to view formulary coverage for this medication.
          </BodyText>
          {data.coverages.map((coverage) => (
            <button
              key={coverage.coverageId}
              type="button"
              className="flex flex-col gap-0.5 rounded-md border p-3 text-left hover:bg-secondary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={coverage.patientEligibilityId == null}
              onClick={() => {
                if (coverage.patientEligibilityId != null) {
                  setSelectedPayer({
                    patientEligibilityId: coverage.patientEligibilityId,
                    payerName: coverage.payerName,
                  });
                }
              }}
            >
              <BodyText size="sm" className="font-medium">
                {coverage.payerName}
              </BodyText>
              {coverage.planName && (
                <BodyText size="sm" textColor="tertiary">
                  {coverage.planName}
                </BodyText>
              )}
              {coverage.patientEligibilityId == null && (
                <BodyText size="sm" textColor="error">
                  No eligibility ID — cannot look up formulary
                </BodyText>
              )}
            </button>
          ))}
        </div>
      );
    }

    return null;
  };

  const title = selectedPayer
    ? (medicationName ?? "Formulary")
    : "Select Payer";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogOverlay />
      <DialogContent className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="hidden">
            Formulary information for this medication
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
};
